/*
 * Copyright © 2015-2018 Serenova, LLC. All rights reserved.
 */

import { fromJS } from 'immutable';
import { selectInteractionIndexById } from './selectors';
import moment from 'moment';

// Initial Sub State
export const constructInitialState = () => {
  let initialState = {
    sessionId: null,
    data: [],
    sorted: [],
    expanded: {},
    selected: '',
    extensions: [],
    monitorAllCallsPermission: ['MONITOR_ALL_CALLS'],
    bargeAllCallsPermission: ['BARGE_ALL_CALLS'],
    viewAllMonitoredCallsPermission: ['VIEW_ALL_MONITORED_CALLS'],
    interactionsDetailsExpanded: false,
    activeParticipantExpanded: false,
    interactionMonitoringExpanded: false,
  };
  // We want to keep the previous sessionId stored in localStorage. This handles cases where the
  // SupervisorToolbar gets destroyed + rebuilt when leaving and returning to the
  // interactionMonitoring page.
  let storedState = localStorage.getItem('SupervisorToolbar');
  if (storedState !== 'undefined' && storedState != null) {
    storedState = JSON.parse(storedState);
    let storedSessionId = storedState.silentMonitoring && storedState.silentMonitoring.sessionId;
    if (storedSessionId) {
      initialState.sessionId = storedSessionId;
    }
  }
  return fromJS(initialState);
};

// Actions
export const updateTableData = (arrayOfTableData) => ({
  type: 'SET_TABLE_DATA',
  arrayOfTableData,
});
export const setExpanded = (expanded) => ({ type: 'SET_EXPANDED', expanded });
export const setInteractionsDetailExpand = (interactionsDetailsExpanded) => ({
  type: 'SET_INTERACTION_DETAILS_EXPANDED',
});
export const setActiveParticipantsExpand = () => ({ type: 'SET_ACTIVE_PARTICIPANTS_EXPANDED' });
export const setInteractionMonitoringExpanded = () => ({ type: 'SET_INTERACTION_MONITORING_EXPANDED' });
export const setSelected = (selected, expanded) => ({
  type: 'SET_SELECTED',
  selected,
  expanded,
});
export const setSorted = (sorted) => ({ type: 'SET_SORTED', sorted });
export const removeSelected = () => ({ type: 'REMOVE_SELECTED' });
export const getExtensions = () => ({ type: 'GET_USER_EXTENSIONS' });
export const startInteractionMonitoring = () => ({
  type: 'START_REPORTING_BATCH_REQUEST_$',
});
export const startReportingSubscriptions = () => ({
  type: 'START_REPORTING_SUBSCRIPTION_$',
});
export const reportingSubscriptionStarted = () => ({
  type: 'REPORTING_SUBSCRIPTION_STARTED_$',
});
export const interactionMonitoringSubscriptionAdded = (subscription) => ({
  type: 'INTERACTION_MONITORING_SUBSCRIPTION_ADDED_$',
  subscription,
});
export const messagingMonitorStarted = (interactionId) => ({
  type: 'START_MESSAGE_HISTORY_$',
  interactionId,
});
export const monitorMessageInteraction = (interactionId, channelType) => ({
  type: 'REQUESTING_MONITOR_SESSION_INITIALIZATION',
  interactionId,
  channelType,
});
export const requestingSessionInitializationCompleted = (interactionId) => ({
  type: 'REQUESTING_MONITOR_SESSION_INITIALIZATION_COMPLETED_$',
  interactionId,
});
export const endMonitorMessageInteraction = (interactionId) => ({
  type: 'REQUESTING_MONITOR_MESSAGING_END',
  interactionId,
});
export const onSeenNewestMessage = (interactionId) => ({
  type: 'SEEN_NEWEST_MESSAGE',
  interactionId,
});
export const setLastScrollPosition = (interactionId, scrollPosition) => ({
  type: 'SET_LAST_SCROLL_POSITION',
  interactionId,
  scrollPosition,
});
export const clearMonitorStates = () => ({
  type: 'CLEAR_MONITOR_STATES',
});

// Reducer
export default function InteractionMonitoring(state = constructInitialState(), action) {
  switch (action.type) {
    case 'SET_EXTENSION_LIST':
      return state.set('extensions', fromJS(action.extensions)).delete('isFetchingUserExtensions');
    case 'SET_TABLE_DATA':
      // Do a careful merge when a matching interaction is found in the current data list.
      // This keeps newMessageCount, monitorState, messageHistory, etc. from being deleted every update,
      // while also preventing immutable from duplicating interactions.
      const oldTableData = state.get('data');
      const newTableData = fromJS(action.arrayOfTableData).map((newInteraction) => {
        const matchingOldInteraction = oldTableData.find(
          (oldInteraction) => oldInteraction.get('interactionId') === newInteraction.get('interactionId')
        );
        if (matchingOldInteraction) {
          return matchingOldInteraction.mergeDeep(newInteraction);
        } else {
          return newInteraction;
        }
      });
      return state.set('data', newTableData);
    case 'SET_SORTED':
      return state
        .set('sorted', action.sorted)
        .set('selected', '')
        .set('expanded', {});
    case 'SET_EXPANDED':
      return state.set('expanded', action.expanded);
    case 'SET_INTERACTION_DETAILS_EXPANDED':
      return state.set('interactionsDetailsExpanded', !state.get('interactionsDetailsExpanded'));
    case 'SET_ACTIVE_PARTICIPANTS_EXPANDED':
      return state.set('activeParticipantExpanded', !state.get('activeParticipantExpanded'));
    case 'SET_INTERACTION_MONITORING_EXPANDED':
      return state.set('interactionMonitoringExpanded', !state.get('interactionMonitoringExpanded'));
    case 'SET_SELECTED': {
      return state.set('selected', action.selected).set('expanded', action.expanded);
    }
    case 'REMOVE_SELECTED':
      return state.set('expanded', {}).set('selected', '');
    case 'GET_USER_EXTENSIONS':
      return state.set('isFetchingUserExtensions', true);
    case 'REQUESTING_MONITOR_SESSION_INITIALIZATION': {
      const interactionIndex = selectInteractionIndexById(state, action.interactionId);
      if (interactionIndex < 0) {
        return state;
      }
      return state.setIn(['data', interactionIndex, 'monitorState'], 'starting');
    }
    case 'SEEN_NEWEST_MESSAGE': {
      const interactionIndex = selectInteractionIndexById(state, action.interactionId);
      if (interactionIndex < 0) {
        return state;
      }
      return state.setIn(['data', interactionIndex, 'newMessageCount'], 0);
    }
    case 'SET_LAST_SCROLL_POSITION': {
      const interactionIndex = selectInteractionIndexById(state, action.interactionId);
      if (interactionIndex < 0) {
        return state;
      }
      return state.setIn(['data', interactionIndex, 'lastScrollPosition'], action.scrollPosition);
    }
    case 'CLEAR_MONITOR_STATES': {
      return state.update('data', (interactions) =>
        interactions.map((interaction) => interaction.set('monitorState', 'ended'))
      );
    }
    case 'cxengage/session/started':
      return state.set('sessionId', action.response && action.response.sessionId);
    case 'cxengage/interactions/messaging/silent-monitor-start-acknowledged':
      const interactionIndex = selectInteractionIndexById(state, action.response && action.response.interactionId);
      if (interactionIndex < 0) {
        return state;
      }
      return state.setIn(['data', interactionIndex, 'monitorState'], 'started');
    case 'cxengage/interactions/messaging/silent-monitor-end-acknowledged': {
      const interactionIndex = selectInteractionIndexById(state, action.response && action.response.interactionId);
      if (interactionIndex < 0) {
        return state;
      }
      return state.setIn(['data', interactionIndex, 'monitorState'], 'ended');
    }
    case 'cxengage/interactions/messaging/silent-monitor-message-history': {
      const { interactionId, messageHistory, channelType } = action.response;
      const interactionIndex = selectInteractionIndexById(state, interactionId);
      if (interactionIndex < 0) {
        console.warn('No matching interaction ' + interactionId + ' for message history update.');
        return state;
      }
      return state.updateIn(['data', interactionIndex], (interaction) => {
        let newMessageHistory = messageHistory;
        if (channelType === 'sms') {
          // SMS messages need special treatment
          newMessageHistory = messageHistory.map((message) => {
            const { body, timestamp, metadata } = message;
            if (typeof timestamp === 'string') {
              message.timestamp = +moment(timestamp).format('x');
            }
            if (body?.constructor?.name === 'Object') {
              message = Object.assign(message, body);
            }

            // Normalize attachment payload shape for InteractionMonitoring MessageContent.
            // Source payload may provide attachments as `attachment` or `body.attachment` with
            // fields like { url, contentType, artifactId }.
            const rawAttachments =
              (Array.isArray(message.attachment) && message.attachment) ||
              (Array.isArray(body?.attachment) && body.attachment) ||
              [];

            if (rawAttachments.length > 0) {
              const normalizedAttachments = rawAttachments.map((attachment) => {
                const mediaUrl = attachment.mediaUrl || attachment.url;
                const mediaType = attachment.mediaType || attachment.contentType;
                const fileName =
                  attachment.fileName ||
                  (mediaUrl && decodeURIComponent(mediaUrl).split('?')[0].split('/').filter(Boolean).pop()) ||
                  attachment.artifactId;
                return {
                  mediaUrl,
                  mediaType,
                  fileName,
                };
              });

              message.attachments = normalizedAttachments;

              // Force deterministic content type for SMS attachment payloads.
              // This avoids inconsistent rendering where similar messages can be marked
              // as 'image' or 'mixed' depending on backend payload variance.
              const hasText =
                typeof message.text === 'string'
                  ? message.text.trim().length > 0
                  : !!message.text;

              if (hasText || normalizedAttachments.length > 1) {
                message.contentType = 'mixed';
              } else {
                const firstType = normalizedAttachments[0] && normalizedAttachments[0].mediaType;
                message.contentType = firstType && firstType.startsWith('image/') ? 'image' : 'file';
              }
            }

            // see: agent-desktop/app/models/Message/ResponseMessage.js
            if (metadata) {
              if (metadata.type === 'agent') {
                message.type = 'agent';
              } else if (metadata.type == null) {
                message.type = 'system';
                message.from = 'system';
              }
            }
            return message;
          });
        }
        // Don't show new message icon if interaction is already expanded
        // Should never be unset from here, only by the user viewing the new messages
        const isExpanded = Object.values(state.get('expanded')).includes(interactionId);
        const unviewedMessages = interaction.get('newMessageCount') > 0;
        let newMessageCount =
          unviewedMessages + (newMessageHistory.length - (interaction.get('messageHistory') || []).length);
        if (!isExpanded && newMessageCount > 0) {
          return interaction.set('messageHistory', newMessageHistory).set('newMessageCount', newMessageCount);
        } else {
          return interaction.set('messageHistory', newMessageHistory);
        }
      });
    }
    default:
      return state;
  }
}
